import React, { useState } from 'react';
import { 
  Download, ChevronRight, ShoppingCart, 
  Box, Maximize2, Layers, Cpu, FileText, CheckCircle 
} from 'lucide-react';
import { formatPrice } from './Products';

const ProductDetail = ({ product, onBack }) => {
  const [activeTab, setActiveTab] = useState('desc');

  // خواندن اطلاعات از دیتابیس (یا استفاده از مقدار پیش‌فرض اگر خالی بود)
  const technicalSpecs = {
    polys: product.polys || "نامشخص",
    verts: product.verts || "نامشخص",
    formats: product.formats ? product.formats.split(',') : [product.renderer], 
    renderers: [product.renderer],
    textures: product.textures || "نامشخص",
    uvMapping: product.uv_mapping || "Yes"
  };

  const description = product.description || `
    این ${product.title} یکی از دقیق‌ترین مدل‌های سه‌بعدی موجود در آرشیو است.
    برای اطلاعات بیشتر لطفا مشخصات فنی را مطالعه کنید.
  `;

  // --- تغییر انجام شده در این بخش است ---
  const handleActionClick = () => {
    if (product.is_free) {
        if (product.download_link) {
            // ایجاد یک لینک مخفی برای اجبار به دانلود
            const link = document.createElement('a');
            link.href = product.download_link;
            // ویژگی download به مرورگر می‌گوید فایل را دانلود کن نه اینکه نمایش بده
            link.setAttribute('download', product.title || 'download');
            link.setAttribute('target', '_blank'); // برای اطمینان
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            alert("لینک دانلود برای این محصول ثبت نشده است.");
        }
    } else {
        // اینجا منطق افزودن به سبد خرید قرار می‌گیرد
        alert("محصول به سبد خرید اضافه شد (دمو)");
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen animate-fade-in-up pb-20 pt-36">
      
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 mb-6 flex items-center gap-2 text-sm text-slate-500">
        <button onClick={onBack} className="hover:text-blue-600 flex items-center gap-1 transition-colors font-bold">
          <ChevronRight size={16} />
          بازگشت
        </button>
        <span className="text-slate-300">/</span>
        <span className="text-slate-800 font-bold">{product.title}</span>
      </div>

      <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* --- Left Column: Images --- */}
        <div className="lg:col-span-8 space-y-8">
          <div className="relative w-full h-[400px] md:h-[600px] bg-white rounded-[2.5rem] overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-100 group">
            <img 
              src={product.image} 
              alt={product.title} 
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            
            <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-md px-5 py-2 rounded-2xl text-xs font-bold shadow-lg border border-white/50 text-slate-800">
              {product.renderer}
            </div>
          </div>

          {/* Tabs */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-1 border border-slate-200 inline-flex shadow-sm">
                <button 
                onClick={() => setActiveTab('desc')}
                className={`px-6 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'desc' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
                >
                توضیحات
                </button>
                <button 
                onClick={() => setActiveTab('specs')}
                className={`px-6 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'specs' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
                >
                مشخصات فنی
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-[2rem] p-8 leading-8 text-slate-600 text-justify shadow-sm min-h-[200px]">
                {activeTab === 'desc' ? (
                <div className="animate-fadeIn">
                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <FileText className="text-blue-500" size={24} />
                    نقد و بررسی
                    </h3>
                    <p className="whitespace-pre-line">{description}</p>
                </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <span className="text-slate-500 text-sm">Polygons:</span>
                        <span className="font-bold text-slate-800 dir-ltr">{technicalSpecs.polys}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <span className="text-slate-500 text-sm">Vertices:</span>
                        <span className="font-bold text-slate-800 dir-ltr">{technicalSpecs.verts}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <span className="text-slate-500 text-sm">Formats:</span>
                        <span className="font-bold text-slate-800 dir-ltr">
                             {Array.isArray(technicalSpecs.formats) ? technicalSpecs.formats.join(", ") : technicalSpecs.formats}
                        </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <span className="text-slate-500 text-sm">Textures:</span>
                        <span className="font-bold text-slate-800 dir-ltr">{technicalSpecs.textures}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <span className="text-slate-500 text-sm">UV Mapping:</span>
                        <span className="font-bold text-slate-800 dir-ltr">{technicalSpecs.uvMapping}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <span className="text-slate-500 text-sm">Renderer:</span>
                        <span className="font-bold text-slate-800 dir-ltr">{product.renderer}</span>
                    </div>
                </div>
                )}
            </div>
          </div>
        </div>

        {/* --- Right Column: Sticky Sidebar --- */}
        <div className="lg:col-span-4 relative">
          <div className="sticky top-32 space-y-6">
            
            {/* Info Card */}
            <div className="bg-white rounded-[2rem] p-6 shadow-xl shadow-slate-200/60 border border-slate-200/60">
              <h1 className="text-2xl font-black text-slate-800 mb-3 leading-tight">
                {product.title}
              </h1>
              
              <div className="flex items-center justify-between mb-8 border-b border-slate-100 pb-4">
                 <span className="text-slate-400 text-xs font-medium bg-slate-50 px-3 py-1 rounded-lg">کد: {product.id + 1024}</span>
                 <div className="text-right">
                    {product.is_free ? (
                        <span className="text-green-600 font-black text-xl">رایگان</span>
                    ) : (
                        <div className="flex flex-col items-end">
                            <span className="text-slate-400 text-[10px] line-through mb-1">{formatPrice(product.price * 1.2)}</span>
                            <span className="text-slate-900 font-black text-xl">{formatPrice(product.price)}</span>
                        </div>
                    )}
                 </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                 <div className="bg-slate-50 p-4 rounded-2xl flex flex-col items-center justify-center gap-2 border border-slate-100">
                    <Box size={22} className="text-blue-500" />
                    <span className="text-xs font-bold text-slate-600">{technicalSpecs.polys} Poly</span>
                 </div>
                 <div className="bg-slate-50 p-4 rounded-2xl flex flex-col items-center justify-center gap-2 border border-slate-100">
                    <Layers size={22} className="text-purple-500" />
                    <span className="text-xs font-bold text-slate-600">{product.file_size}</span>
                 </div>
              </div>

              {/* Action Button */}
              <button 
                  onClick={handleActionClick}
                  className={`w-full py-4 rounded-2xl font-bold text-lg shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-3 ${
                  product.is_free 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/30' 
                  : 'bg-slate-900 hover:bg-slate-800 text-white shadow-slate-900/30'
              }`}>
                  {product.is_free ? (
                      <>
                        <Download size={24} />
                        <span>دانلود مستقیم</span>
                      </>
                  ) : (
                      <>
                        <ShoppingCart size={24} />
                        <span>افزودن به سبد خرید</span>
                      </>
                  )}
              </button>

              {!product.is_free && (
                 <div className="mt-4 p-4 bg-orange-50 border border-orange-100 rounded-2xl flex items-start gap-3">
                    <div className="bg-orange-100 p-1.5 rounded-full text-orange-600 mt-0.5">
                        <CheckCircle size={14} />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-orange-800 mb-1">گارانتی بازگشت وجه</p>
                        <p className="text-[10px] text-orange-600 leading-4">در صورت مغایرت فایل با توضیحات، وجه شما عودت داده می‌شود.</p>
                    </div>
                 </div>
              )}
            </div>

            {/* Render Engines Info */}
            <div className="bg-slate-800 rounded-[2rem] p-6 text-white shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <h4 className="font-bold text-sm mb-4 text-slate-300 relative z-10">پلتفرم‌های قابل اجرا:</h4>
                <div className="flex flex-wrap gap-2 relative z-10">
                    {technicalSpecs.renderers.map((eng, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-xl border border-white/5 hover:bg-white/20 transition-colors cursor-default">
                            <Cpu size={16} className="text-blue-400" />
                            <span className="text-xs font-bold">{eng}</span>
                        </div>
                    ))}
                </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;